import { Listing } from '@df/catalog-ui/listing/listing';
import { AbstractAsyncEngine, ISortOptions } from '@df/catalog-ui/listing/sort/abstract-async.engine';
import { IListingSortEngine, ListingSort } from '@df/catalog-ui/listing/sort/listing-sort';
import { Category, ICategoryDataV3 } from '@df/catalog/category/category';
import { Product } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { NgInject } from '@df/ng/ng-inject';
import compact from 'lodash-es/compact';
import { PIO_OPTIONS } from '../config/pio-options';
import { IPioEngineRequestField, IPioLoadOptions, IPioOptions } from '../core/interfaces';
import { PioRecommendService } from '../core/pio-recommend.service';

export class ListingSortPioEngine extends AbstractAsyncEngine implements IListingSortEngine<Product> {
    @NgInject(PioRecommendService)
    private pioRecommendService!: PioRecommendService;

    @NgInject(PIO_OPTIONS)
    private pioConfig!: IPioOptions;

    @NgInject(App)
    private app!: App;

    protected override isOn(listing: Listing<Product>): boolean {
        // do not apply if globally disabled
        if (!this.pioConfig.listing.enabled) {
            return false;
        }

        // do not apply when in dev mode and not developer
        if (this.pioConfig.listing.devOnly && !this.app.acl.isDeveloper) {
            return false;
        }

        // check if allowed
        if (!listing.config.custom || !listing.config.custom.pioAllowed) {
            return false;
        }

        if (!super.isOn(listing)) {
            return false;
        }

        const sourceModel: Category<ICategoryDataV3> | undefined = listing.config.source;

        // do not apply if category does not allow it
        if (!sourceModel?.data?.['pio_enabled']) {
            return false;
        }

        return true;
    }

    /**
     * Retrieve sort options
     */
    protected override getSortOptions(listing: Listing<Product>): ISortOptions {
        const sortOptions = super.getSortOptions(listing);
        const sourceModel: Category<ICategoryDataV3> | undefined = listing.config.source;
        sortOptions.banDiscounted = sourceModel?.data?.['pio_ban_discounted'];

        if (!sourceModel?.data?.['pio_respect_heroes']) {
            sortOptions.heroes = undefined;
        }

        return sortOptions;
    }

    protected override fetch(listing: Listing<Product>, collection: Product[], cb: (err: Error | string | null, ids: number[]) => void): void {
        const sourceModel: Category<ICategoryDataV3> | undefined = listing.config.source;

        const options: IPioLoadOptions = {
            engine: 'universal',
            user: this.pioConfig.listing.user
        };

        // assign category filter and boost
        options.category = <number>sourceModel?.id;
        options.boost = [
            ...this.prepareBoostFromCategory(sourceModel),
            ...this.prepareNewInBoostFields(sourceModel?.data?.['pio_new_boost'] || false)
        ];

        options.limit = collection.length;

        const promise = this.pioRecommendService.load(undefined, options);

        if (!promise) {
            cb('No promise', []);
        } else {
            promise.then(result => cb(null, result.products)).catch(err => cb(err, []));
        }
    }

    /**
     * Takes direct boost values from categorty attribute `pio_custom`
     *
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {Category}               model
     * @return {IPioEngineRequestField}
     */
    protected prepareBoostFromCategory(model?: Category<ICategoryDataV3>): IPioEngineRequestField[] {
        if (model && model.data) {
            const boost = model.data?.['pio_custom'];
            if (boost) {
                boost.forEach(item => (item.values = <string[]>item.values.map((i: number | string) => '' + i)));
                return boost;
            }
        }
        return [];
    }

    /**
     * Takes value for current period id  used with new in boost
     *
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     */
    protected getCurrentNewIndexPeriod() {
        return Math.floor((Date.now() - this.app.data?.['pio_config']?.start) / 86400 / 1000 / this.app.data?.['pio_config']?.days);
    }

    /**
     * Prepares boost values for newindex
     * @return array|undefined
     */
    protected prepareNewInBoostFields(boostValue: string | false = '0.1;0.05;0.01'): IPioEngineRequestField[] {
        const currentPeriod = this.getCurrentNewIndexPeriod();
        if (boostValue && currentPeriod) {
            return compact<IPioEngineRequestField>(
                boostValue
                    .toString()
                    .replace(/\s/g, '')
                    .split(';', 3)
                    .map((boostString, index): IPioEngineRequestField | undefined => {
                        const _boostValue = parseFloat(boostString);
                        const appendixMap = ['+', '', '-'];
                        if (_boostValue && index < 3) {
                            return {
                                name: 'newindex',
                                values: [currentPeriod + appendixMap[index]],
                                bias: _boostValue
                            };
                        }
                        return undefined;
                    })
            );
        }
        return [];
    }
}

ListingSort.engines['pio'] = new ListingSortPioEngine();
